import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ContactService } from '../../api/generated/services/ContactService';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';

export interface ContactUsData {
  fullName: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  dataProcessingConsent: boolean;
  captchaToken?: string | null;
}

export const sendContactUs = createAsyncThunk(
  'contactUs/send',
  async (contactData: ContactUsData, { rejectWithValue }) => {
    try {
      const response = await ContactService.postAppContactSubmit({
        name: contactData.fullName,
        email: contactData.email,
        phone: contactData.phone || null,
        message: contactData.message,
        captchaToken: contactData.captchaToken || null,
      });
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 422) {
          return rejectWithValue('captcha_failed');
        }
        if (error.status === 429) {
          return rejectWithValue('rate_limit');
        }
        const parsedBody = helper.parseApiErrorBody(error);
        if (parsedBody && parsedBody.error) {
          return rejectWithValue(parsedBody.error);
        }
      }
      return rejectWithValue('unknown_error');
    }
  }
);

interface ContactUsState {
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: ContactUsState = {
  loading: false,
  error: null,
  success: false,
};

const contactUsSlice = createSlice({
  name: 'contactUs',
  initialState,
  reducers: {
    resetContactUs: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendContactUs.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(sendContactUs.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.success = true;
      })
      .addCase(sendContactUs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  },
});

export const { resetContactUs } = contactUsSlice.actions;
export default contactUsSlice.reducer;
